import React, { useState } from 'react';
import { Lightning, Barbell, UserCircle, ArrowRight, ArrowLeft, Check, Upload, FileText, Warning } from '@phosphor-icons/react';
import { api } from '../data/api';

const GOALS = [
  { id: 'fat_loss', name: 'Fat Loss', desc: 'Burn fat, get lean', icon: '🔥', color: '#FF3B30' },
  { id: 'muscle_gain', name: 'Muscle Gain', desc: 'Build mass & size', icon: '💪', color: '#007AFF' },
  { id: 'strength', name: 'Strength', desc: 'Move heavy weight', icon: '🏋️', color: '#8B5CF6' },
  { id: 'athletic', name: 'Athletic', desc: 'Speed, power, agility', icon: '⚡', color: '#FFD60A' },
  { id: 'general_fitness', name: 'General Fitness', desc: 'Health & balance', icon: '🧘', color: '#32D74B' },
  { id: 'centurion', name: 'Centurion', desc: 'Calisthenics god mode', icon: '🏛️', color: '#FF6B35' },
];

const EXPERIENCE = [
  { id: 'beginner', name: 'Beginner', desc: 'Less than 6 months training' },
  { id: 'intermediate', name: 'Intermediate', desc: '6 months to 2 years' },
  { id: 'advanced', name: 'Advanced', desc: '2+ years consistent training' },
];

const EQUIPMENT = [
  { id: 'full_gym', name: 'Full Gym', desc: 'Barbells, cables, machines' },
  { id: 'home_dumbbells', name: 'Home Dumbbells', desc: 'Dumbbells + bench' },
  { id: 'bodyweight', name: 'Bodyweight Only', desc: 'No equipment needed' },
  { id: 'mixed', name: 'Mixed / Minimal', desc: 'Pull-up bar, bands, etc.' },
];

const FOCUS_OPTIONS = ['Chest', 'Back', 'Shoulders', 'Arms', 'Core', 'Legs', 'Glutes', 'Full Body'];

export default function PlanBuilder({ user, onComplete, showToast }) {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState('questions'); // 'questions' | 'upload'
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Questionnaire state
  const [goal, setGoal] = useState('');
  const [experience, setExperience] = useState('');
  const [daysPerWeek, setDaysPerWeek] = useState(4);
  const [sessionMinutes, setSessionMinutes] = useState(60);
  const [equipment, setEquipment] = useState('');
  const [focusAreas, setFocusAreas] = useState([]);
  const [limitations, setLimitations] = useState('');

  // Upload state
  const [programText, setProgramText] = useState('');
  const [uploadFile, setUploadFile] = useState(null);

  const totalSteps = mode === 'questions' ? 5 : 2;

  const toggleFocus = (area) => {
    setFocusAreas(prev => prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]);
  };

  const canProceed = () => {
    if (mode === 'upload') {
      if (step === 1) return programText.trim().length > 10 || uploadFile;
      return true;
    }
    if (step === 1) return !!goal;
    if (step === 2) return !!experience;
    if (step === 3) return daysPerWeek >= 3 && daysPerWeek <= 6;
    if (step === 4) return !!equipment;
    return true;
  };

  const handleGenerate = async () => {
    setError('');
    setGenerating(true);
    try {
      let result;
      if (mode === 'upload') {
        const formData = new FormData();
        formData.append('user_id', user.user_id);
        formData.append('goal', goal || 'general_fitness');
        formData.append('weight', user.weight);
        formData.append('bf', user.bf);
        formData.append('height', user.height);
        formData.append('unit', user.unit || 'metric');
        formData.append('days_per_week', daysPerWeek);
        formData.append('equipment', equipment || 'full_gym');
        formData.append('program_text', programText);
        if (uploadFile) formData.append('file', uploadFile);
        result = await api.uploadProgram(formData);
      } else {
        result = await api.generatePlan({
          user_id: user.user_id,
          goal, experience, days_per_week: daysPerWeek,
          session_minutes: sessionMinutes, equipment,
          focus_areas: focusAreas, limitations,
          weight: user.weight, bf: user.bf, height: user.height,
          unit: user.unit || 'metric',
        });
      }
      if (showToast) showToast('Your custom plan is ready', 'fire');
      onComplete(result.program);
    } catch (err) {
      setError(err.message || 'Plan generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Generating overlay
  if (generating) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center p-6" data-testid="plan-generating">
        <div className="text-center max-w-sm">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 border-2 border-brand/30 rounded-full" />
            <div className="absolute inset-0 border-2 border-brand border-t-transparent rounded-full animate-spin" />
            <div className="absolute inset-2 border-2 border-accent/30 rounded-full" />
            <div className="absolute inset-2 border-2 border-accent border-b-transparent rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
          </div>
          <div className="font-head text-xl font-bold uppercase text-white mb-2">Building Your Plan</div>
          <div className="text-sm text-neutral-400 leading-relaxed">
            GPT-5.2 is designing your program...
            <br />Claude Opus is reviewing for safety & balance.
          </div>
          <div className="mt-4 text-[10px] text-neutral-600 uppercase tracking-wider">This may take 30-60 seconds</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base p-4 flex flex-col" data-testid="plan-builder">
      {/* Header */}
      <div className="text-center mb-4">
        <div className="font-head text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">Build Your Plan</div>
        <div className="text-xs text-neutral-500 uppercase tracking-wider mt-1">
          {mode === 'questions' ? `Step ${step} of ${totalSteps}` : `Upload — Step ${step} of ${totalSteps}`}
        </div>
      </div>

      {/* Progress */}
      <div className="flex gap-1 mb-4">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i < step ? 'bg-brand' : 'bg-border'}`} />
        ))}
      </div>

      {/* Mode toggle (only on step 1) */}
      {step === 1 && (
        <div className="flex gap-2 mb-4">
          <button onClick={() => { setMode('questions'); setStep(1); }}
            className={`flex-1 py-2 rounded-md text-xs font-bold uppercase border transition-all ${
              mode === 'questions' ? 'bg-brand text-white border-brand' : 'bg-elevated text-neutral-500 border-border'
            }`} data-testid="mode-questions">
            Answer Questions
          </button>
          <button onClick={() => { setMode('upload'); setStep(1); }}
            className={`flex-1 py-2 rounded-md text-xs font-bold uppercase border transition-all ${
              mode === 'upload' ? 'bg-brand text-white border-brand' : 'bg-elevated text-neutral-500 border-border'
            }`} data-testid="mode-upload">
            Upload Program
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-md mb-3 flex items-center gap-2" data-testid="plan-error">
          <Warning size={16} weight="bold" /> {error}
        </div>
      )}

      {/* Content */}
      <div className="flex-1">
        {/* === QUESTIONS MODE === */}
        {mode === 'questions' && step === 1 && (
          <div className="space-y-2" data-testid="step-goal">
            <div className="text-sm font-bold uppercase text-neutral-400 mb-3">What's your mission?</div>
            {GOALS.map(g => (
              <button key={g.id} onClick={() => setGoal(g.id)}
                className={`w-full p-3 rounded-md border-2 text-left transition-all active:scale-[0.98] flex items-center gap-3 ${
                  goal === g.id ? 'border-brand bg-brand/10' : 'border-border bg-surface'
                }`} data-testid={`goal-${g.id}`}>
                <span className="text-2xl">{g.icon}</span>
                <div className="flex-1">
                  <div className="font-head text-sm font-bold uppercase text-white">{g.name}</div>
                  <div className="text-[10px] text-neutral-500">{g.desc}</div>
                </div>
                {goal === g.id && <Check size={18} weight="bold" className="text-brand" />}
              </button>
            ))}
          </div>
        )}

        {mode === 'questions' && step === 2 && (
          <div className="space-y-2" data-testid="step-experience">
            <div className="text-sm font-bold uppercase text-neutral-400 mb-3">Training experience?</div>
            {EXPERIENCE.map(e => (
              <button key={e.id} onClick={() => setExperience(e.id)}
                className={`w-full p-4 rounded-md border-2 text-left transition-all active:scale-[0.98] ${
                  experience === e.id ? 'border-brand bg-brand/10' : 'border-border bg-surface'
                }`} data-testid={`exp-${e.id}`}>
                <div className="font-head text-sm font-bold uppercase text-white">{e.name}</div>
                <div className="text-xs text-neutral-500 mt-0.5">{e.desc}</div>
                {experience === e.id && <Check size={16} weight="bold" className="text-brand mt-1" />}
              </button>
            ))}
          </div>
        )}

        {mode === 'questions' && step === 3 && (
          <div className="space-y-5" data-testid="step-schedule">
            <div>
              <div className="text-sm font-bold uppercase text-neutral-400 mb-3">Days per week</div>
              <div className="flex gap-2">
                {[3, 4, 5, 6].map(d => (
                  <button key={d} onClick={() => setDaysPerWeek(d)}
                    className={`flex-1 h-14 rounded-md font-head text-xl font-bold border-2 transition-all active:scale-95 ${
                      daysPerWeek === d ? 'border-brand bg-brand/10 text-white' : 'border-border bg-surface text-neutral-500'
                    }`} data-testid={`days-${d}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-sm font-bold uppercase text-neutral-400 mb-3">Session length</div>
              <div className="flex gap-2">
                {[30, 45, 60, 90].map(m => (
                  <button key={m} onClick={() => setSessionMinutes(m)}
                    className={`flex-1 h-14 rounded-md font-mono text-base font-bold border-2 transition-all active:scale-95 ${
                      sessionMinutes === m ? 'border-brand bg-brand/10 text-white' : 'border-border bg-surface text-neutral-500'
                    }`} data-testid={`mins-${m}`}>
                    {m}<span className="text-[10px] text-neutral-600">min</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {mode === 'questions' && step === 4 && (
          <div className="space-y-2" data-testid="step-equipment">
            <div className="text-sm font-bold uppercase text-neutral-400 mb-3">Equipment available?</div>
            {EQUIPMENT.map(e => (
              <button key={e.id} onClick={() => setEquipment(e.id)}
                className={`w-full p-4 rounded-md border-2 text-left transition-all active:scale-[0.98] ${
                  equipment === e.id ? 'border-brand bg-brand/10' : 'border-border bg-surface'
                }`} data-testid={`equip-${e.id}`}>
                <div className="font-head text-sm font-bold uppercase text-white">{e.name}</div>
                <div className="text-xs text-neutral-500 mt-0.5">{e.desc}</div>
                {equipment === e.id && <Check size={16} weight="bold" className="text-brand mt-1" />}
              </button>
            ))}
          </div>
        )}

        {mode === 'questions' && step === 5 && (
          <div className="space-y-4" data-testid="step-focus">
            <div>
              <div className="text-sm font-bold uppercase text-neutral-400 mb-3">Focus areas (optional)</div>
              <div className="flex flex-wrap gap-2">
                {FOCUS_OPTIONS.map(f => (
                  <button key={f} onClick={() => toggleFocus(f)}
                    className={`px-3 py-2 rounded-full text-xs font-bold uppercase border transition-all active:scale-95 ${
                      focusAreas.includes(f) ? 'bg-brand text-white border-brand' : 'bg-surface text-neutral-500 border-border'
                    }`} data-testid={`focus-${f.toLowerCase().replace(' ', '-')}`}>
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-sm font-bold uppercase text-neutral-400 mb-2">Injuries / limitations</div>
              <textarea value={limitations} onChange={(e) => setLimitations(e.target.value)}
                placeholder="e.g., bad lower back, shoulder impingement, knee pain..."
                className="w-full bg-surface border border-border text-white text-sm rounded-md px-3 py-2.5 min-h-[80px] resize-none outline-none focus:border-brand"
                data-testid="limitations-input" />
            </div>
          </div>
        )}

        {/* === UPLOAD MODE === */}
        {mode === 'upload' && step === 1 && (
          <div className="space-y-4" data-testid="step-upload">
            <div className="text-sm font-bold uppercase text-neutral-400 mb-2">Paste or upload your program</div>
            <textarea value={programText} onChange={(e) => setProgramText(e.target.value)}
              placeholder="Paste your workout program here... e.g.&#10;&#10;Monday - Chest & Triceps&#10;Bench Press 4x8-10&#10;Incline DB Press 3x12&#10;..."
              className="w-full bg-surface border border-border text-white text-sm rounded-md px-3 py-3 min-h-[160px] resize-none outline-none focus:border-brand font-mono"
              data-testid="program-text-input" />
            <div className="text-center text-xs text-neutral-600 uppercase">or</div>
            <label className="block w-full cursor-pointer" data-testid="file-upload-label">
              <div className={`w-full p-4 rounded-md border-2 border-dashed text-center transition-all ${
                uploadFile ? 'border-success bg-success/5' : 'border-border hover:border-brand/50'
              }`}>
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText size={20} weight="bold" className="text-success" />
                    <span className="text-sm text-success font-bold">{uploadFile.name}</span>
                  </div>
                ) : (
                  <>
                    <Upload size={24} className="text-neutral-500 mx-auto mb-1" />
                    <div className="text-xs text-neutral-500">Upload PDF or text file</div>
                  </>
                )}
              </div>
              <input type="file" accept=".pdf,.txt,.md" className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
            </label>
          </div>
        )}

        {mode === 'upload' && step === 2 && (
          <div className="space-y-4" data-testid="step-upload-config">
            <div className="text-sm font-bold uppercase text-neutral-400 mb-2">Quick preferences</div>
            <div>
              <div className="text-xs text-neutral-500 uppercase mb-1">Goal</div>
              <div className="grid grid-cols-3 gap-1.5">
                {GOALS.map(g => (
                  <button key={g.id} onClick={() => setGoal(g.id)}
                    className={`p-2 rounded-md border text-center transition-all active:scale-95 ${
                      goal === g.id ? 'border-brand bg-brand/10' : 'border-border bg-surface'
                    }`} data-testid={`upload-goal-${g.id}`}>
                    <div className="text-lg">{g.icon}</div>
                    <div className="text-[9px] font-bold uppercase text-neutral-400">{g.name}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-neutral-500 uppercase mb-1">Days/week</div>
                <div className="flex gap-1">
                  {[3,4,5,6].map(d => (
                    <button key={d} onClick={() => setDaysPerWeek(d)}
                      className={`flex-1 h-10 rounded text-sm font-bold border transition-all ${
                        daysPerWeek === d ? 'border-brand bg-brand/10 text-white' : 'border-border text-neutral-500'
                      }`}>{d}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs text-neutral-500 uppercase mb-1">Equipment</div>
                <select value={equipment} onChange={e => setEquipment(e.target.value)}
                  className="w-full h-10 bg-surface border border-border text-white text-xs rounded px-2 outline-none focus:border-brand">
                  {EQUIPMENT.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="space-y-2 pt-4 mt-auto">
        {step < totalSteps ? (
          <button onClick={() => setStep(s => s + 1)} disabled={!canProceed()}
            className={`w-full h-12 sm:h-14 rounded-md font-head font-bold text-sm sm:text-base uppercase tracking-wide flex items-center justify-center gap-2 transition-all active:scale-95 ${
              canProceed() ? 'bg-brand text-white' : 'bg-elevated text-neutral-600 border border-border cursor-not-allowed'
            }`} data-testid="plan-next-btn">
            Next <ArrowRight size={18} weight="bold" />
          </button>
        ) : (
          <button onClick={handleGenerate} disabled={!canProceed()}
            className={`w-full h-12 sm:h-14 rounded-md font-head font-bold text-sm sm:text-base uppercase tracking-wide flex items-center justify-center gap-2 transition-all active:scale-95 ${
              canProceed() ? 'bg-brand text-white' : 'bg-elevated text-neutral-600 border border-border cursor-not-allowed'
            }`} data-testid="plan-generate-btn">
            <Lightning size={20} weight="bold" /> Generate My Plan
          </button>
        )}
        {step > 1 && (
          <button onClick={() => setStep(s => s - 1)}
            className="w-full h-10 rounded-md bg-elevated border border-border text-neutral-400 font-bold uppercase text-xs flex items-center justify-center gap-1 active:scale-95"
            data-testid="plan-back-btn">
            <ArrowLeft size={14} weight="bold" /> Back
          </button>
        )}
      </div>
    </div>
  );
}
